import { CommonModule } from '@angular/common'
import {
  ChangeDetectionStrategy,
  Component,
  Injector,
  OnInit,
  Signal,
  computed,
  inject,
  input,
  runInInjectionContext,
  signal,
} from '@angular/core'
import { FormControl, ReactiveFormsModule, ValidationErrors } from '@angular/forms'

import { format } from 'date-fns'

import { FlxStyleModule, FlxThemeModule } from '@flowx/angular-theme'
import { FlxDatepickerModule, FlxIconModule } from '@flowx/angular-ui-toolkit'
import {
  DataModelType,
  DatepickerTemplateConfig,
  LabelAccessibilityProps,
  getDataModelForProcess,
  getDateTypingPattern,
  getEffectiveDateFormatting,
  getLocalizedDate,
  isValidISO8601,
  relativeToDataModelKey,
  resolveLocale,
} from '@flowx/core-sdk'
import { ThemeComponent } from '@flowx/core-theme'

import { FlxLocalizePipe } from '../../pipes/flx-localize.pipe'
import {
  getComputedValueSignal,
  getDynamicPropSignal,
  getErrorMessages,
  getThemeIcon,
} from '../helpers'

@Component({
  selector: 'flx-datepicker-wrapper',
  imports: [
    CommonModule,
    FlxDatepickerModule,
    FlxLocalizePipe,
    ReactiveFormsModule,
    FlxThemeModule,
    FlxStyleModule,
    FlxIconModule,
  ],
  templateUrl: './datepicker-wrapper.component.html',
  styleUrl: './datepicker-wrapper.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class FlxDatepickerWrapperComponent implements OnInit {
  #injector = inject(Injector)

  templateConfig = input.required<DatepickerTemplateConfig>()
  control = input.required<FormControl>()
  errors = input<ValidationErrors | null>(null)
  showErrors = input<boolean>(false)
  disabledState = input<boolean>(false)

  displayOptions = computed(() => this.templateConfig().displayOptions)
  flowxProps = computed(() => this.displayOptions().flowxProps)
  fullLocale = computed(() => resolveLocale())
  // language only, for the calendar translation map
  locale = computed(() => this.fullLocale().split('-')[0])
  accessibility = computed((): LabelAccessibilityProps | null => this.flowxProps()?.accessibility)

  icons = computed(() => ({
    clear: getThemeIcon(ThemeComponent.Datepicker, 'clearIcon'),
    rightIcon: getThemeIcon(ThemeComponent.Datepicker, 'rightIcon'),
    leftIcon: getThemeIcon(ThemeComponent.Datepicker, 'leftIcon'),
    helper: getThemeIcon(ThemeComponent.HelperTooltip, 'helperIcon'),
  }))

  // dynamic props
  label: Signal<string>
  placeholder: Signal<string>
  helper: Signal<string>
  errorMessages: Record<string, Signal<string>>
  minDateComputed: Signal<string>
  maxDateComputed: Signal<string>
  ariaLabel: Signal<string>

  // datepicker props
  format = computed(() => (this.flowxProps().standard ? null : undefined))
  displayFormatFn = computed(() =>
    this.flowxProps().standard ? getLocalizedDate(this.templateConfig()) : null
  )
  typingPattern = computed(() =>
    this.flowxProps().standard && this.flowxProps().allowManualInput
      ? getDateTypingPattern(this.fullLocale(), getEffectiveDateFormatting(this.flowxProps()))
      : null
  )
  // `localize` shows a DATE_TIME key as its UTC date
  utcWallClock = computed(() => {
    const { processInstanceUuid, key, prototypeInstanceMeta } = this.templateConfig()
    const dataModelKey = prototypeInstanceMeta
      ? relativeToDataModelKey(prototypeInstanceMeta, key)
      : key
    return (
      !!this.flowxProps().standard &&
      getDataModelForProcess(processInstanceUuid)?.[dataModelKey]?.type === DataModelType.DATE_TIME
    )
  })

  minDate = computed(() => {
    if (this.minDateComputed()) {
      return isValidISO8601(this.minDateComputed())
        ? format(new Date(this.minDateComputed()), 'dd.MM.yyyy')
        : this.minDateComputed()
    }

    return this.flowxProps().minDate
  })
  maxDate = computed(() => {
    if (this.maxDateComputed()) {
      return isValidISO8601(this.maxDateComputed())
        ? format(new Date(this.maxDateComputed()), 'dd.MM.yyyy')
        : this.maxDateComputed()
    }
    return this.flowxProps().maxDate
  })

  readonly elementUniqueId = `${crypto.randomUUID()}`
  readonly labelId = `${this.elementUniqueId}-form-item-label`
  readonly helperId = `${this.elementUniqueId}-helper`
  readonly errorId = `${this.elementUniqueId}-error`

  readonly describedByIds = computed(() => {
    const ids: string[] = []
    const helper = this.helper()
    const showErrors = this.showErrors()
    const errors = this.errors()

    if (showErrors && errors) {
      Object.keys(errors).forEach((_, index) => {
        ids.push(`${this.errorId}${index}`)
      })
    } else if (helper) {
      ids.push(this.helperId)
    }
    return ids.join(' ')
  })

  readonly CLEAR_LABEL = '@@sys_clear'

  ngOnInit(): void {
    // we need to run this in the injection context here because templateConfig is not available in the constructor;
    // also, running in injectionContext will make sure that the observables are properly disposed when the component is destroyed
    runInInjectionContext(this.#injector, () => {
      const config = this.templateConfig()
      const { label, placeholder, helper, isComputedMinDate, isComputedMaxDate, minDate, maxDate } =
        this.flowxProps()
      this.label = getDynamicPropSignal(label, this.templateConfig())
      this.placeholder = getDynamicPropSignal(placeholder, this.templateConfig())
      this.helper = getDynamicPropSignal(helper, this.templateConfig())
      this.errorMessages = getErrorMessages(config)
      this.ariaLabel = getDynamicPropSignal(this.accessibility()?.label, config)

      this.minDateComputed = isComputedMinDate
        ? getComputedValueSignal(minDate, this.templateConfig())
        : signal(null)
      this.maxDateComputed = isComputedMaxDate
        ? getComputedValueSignal(maxDate, this.templateConfig())
        : signal(null)
    })
  }
}
